import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { 
  Check, 
  Zap, 
  Crown,
  Loader2,
  Calendar,
  CreditCard,
  Shield,
  Sparkles,
  XCircle,
  Gift,
  Ticket
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Subscription = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [redeemingPromo, setRedeemingPromo] = useState(false);

  const texts = {
    fr: {
      title: 'Abonnement',
      currentPlan: 'Votre plan actuel',
      freeTrial: 'Essai gratuit',
      trialDays: 'Premier mois gratuit',
      subscribe: 'S\'abonner',
      startTrial: 'Commencer l\'essai gratuit',
      perMonth: '/mois',
      noCommitment: 'Sans engagement',
      cancelAnytime: 'Annulez à tout moment',
      features: 'Fonctionnalités incluses',
      trialActive: 'Essai gratuit actif',
      daysRemaining: 'jours restants',
      subscriptionActive: 'Abonnement actif',
      subscriptionCancelled: 'Abonnement annulé',
      expired: 'Expiré',
      processing: 'Traitement en cours...',
      paymentSuccess: 'Paiement réussi ! Votre abonnement est activé.',
      paymentFailed: 'Le paiement a échoué. Veuillez réessayer.',
      alreadySubscribed: 'Vous avez déjà un abonnement actif',
      popular: 'Populaire',
      howItWorks: 'Comment ça marche ?',
      step1Title: 'Essai gratuit de 30 jours',
      step1Desc: 'Testez toutes les fonctionnalités sans engagement',
      step2Title: 'Facturation mensuelle',
      step2Desc: '9€ HT/mois après l\'essai gratuit',
      step3Title: 'Annulez quand vous voulez',
      step3Desc: 'Sans frais de résiliation',
      securePayment: 'Paiement sécurisé par Stripe',
      cancelSubscription: 'Se désabonner',
      cancelConfirm: 'Êtes-vous sûr de vouloir vous désabonner ?',
      cancelSuccess: 'Votre abonnement a été annulé',
      cancelSuccessTrial: 'Votre essai gratuit a été annulé',
      cancelSuccessEnd: 'Votre abonnement sera annulé à la fin de la période en cours',
      manageSubscription: 'Gérer mon abonnement',
      promoCode: 'Code promo',
      promoPlaceholder: 'XXXX-XXXX-XXXX-XXXX',
      redeemPromo: 'Activer',
      promoSuccess: 'Code activé avec succès !',
      havePromoCode: 'Vous avez un code promo ?',
      featuresList: [
        'Demandes de rappel illimitées',
        'Notifications webhook',
        'Notifications email',
        'Export CSV',
        'Support prioritaire'
      ]
    },
    en: {
      title: 'Subscription',
      currentPlan: 'Your current plan',
      freeTrial: 'Free trial',
      trialDays: 'First month free',
      subscribe: 'Subscribe',
      startTrial: 'Start free trial',
      perMonth: '/month',
      noCommitment: 'No commitment',
      cancelAnytime: 'Cancel anytime',
      features: 'Included features',
      trialActive: 'Free trial active',
      daysRemaining: 'days remaining',
      subscriptionActive: 'Subscription active',
      subscriptionCancelled: 'Subscription cancelled',
      expired: 'Expired',
      processing: 'Processing...',
      paymentSuccess: 'Payment successful! Your subscription is active.',
      paymentFailed: 'Payment failed. Please try again.',
      alreadySubscribed: 'You already have an active subscription',
      popular: 'Popular',
      howItWorks: 'How does it work?',
      step1Title: '30-day free trial',
      step1Desc: 'Test all features with no commitment',
      step2Title: 'Monthly billing',
      step2Desc: '€9 excl. VAT/month after free trial',
      step3Title: 'Cancel anytime',
      step3Desc: 'No cancellation fees',
      securePayment: 'Secure payment by Stripe',
      cancelSubscription: 'Cancel subscription',
      cancelConfirm: 'Are you sure you want to cancel your subscription?',
      cancelSuccess: 'Your subscription has been cancelled',
      cancelSuccessTrial: 'Your free trial has been cancelled',
      cancelSuccessEnd: 'Your subscription will be cancelled at the end of the current period',
      manageSubscription: 'Manage subscription',
      promoCode: 'Promo code',
      promoPlaceholder: 'XXXX-XXXX-XXXX-XXXX',
      redeemPromo: 'Redeem',
      promoSuccess: 'Code activated successfully!',
      havePromoCode: 'Have a promo code?',
      featuresList: [
        'Unlimited callback requests',
        'Webhook notifications',
        'Email notifications',
        'CSV export',
        'Priority support'
      ]
    }
  };

  const t = texts[language] || texts.fr;

  // Map pour traduire les features du backend vers les traductions locales
  const featureTranslationMap = {
    'Unlimited callbacks': {
      fr: 'Rappels illimités',
      en: 'Unlimited callbacks'
    },
    'Webhook notifications': {
      fr: 'Notifications webhook',
      en: 'Webhook notifications'
    },
    'Email notifications': {
      fr: 'Notifications email',
      en: 'Email notifications'
    },
    'CSV export': {
      fr: 'Export CSV',
      en: 'CSV export'
    },
    'Priority support': {
      fr: 'Support prioritaire',
      en: 'Priority support'
    }
  };

  // Fonction pour traduire une feature
  const translateFeature = (feature) => {
    const translation = featureTranslationMap[feature];
    if (translation) {
      return translation[language] || translation.fr;
    }
    return feature; // Retourner la feature originale si pas de traduction
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [plansRes, statusRes] = await Promise.all([
        axios.get(`${API}/subscription/plans`),
        axios.get(`${API}/subscription/status`)
      ]);
      setPlans(plansRes.data.plans);
      setSubscription(statusRes.data);
    } catch (error) {
      console.error('Failed to fetch subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error(t.paymentFailed);
      return;
    }

    try {
      const response = await axios.get(`${API}/subscription/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        toast.success(t.paymentSuccess);
        fetchData();
        window.history.replaceState({}, '', '/subscription');
        return;
      } else if (response.data.status === 'expired') {
        toast.error(t.paymentFailed);
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  const handleStartTrial = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/subscription/start-trial`);
      toast.success(language === 'fr' ? 'Essai gratuit activé !' : 'Free trial activated!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setProcessing(false);
    }
  };

  const handleSubscribe = async (planId) => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/subscription/checkout`, {
        plan_id: planId,
        origin_url: window.location.origin
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
      setProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const response = await axios.post(`${API}/subscription/cancel`);
      if (response.data.cancel_at) {
        toast.success(t.cancelSuccessEnd);
      } else if (subscription?.status === 'trial') {
        toast.success(t.cancelSuccessTrial);
      } else {
        toast.success(t.cancelSuccess);
      }
      setShowCancelModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setCancelling(false);
    }
  };

  const handleRedeemPromo = async () => {
    if (!promoCode.trim()) return;
    
    setRedeemingPromo(true);
    try {
      const response = await axios.post(`${API}/promo/redeem`, { code: promoCode });
      toast.success(response.data.message || t.promoSuccess);
      setPromoCode('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setRedeemingPromo(false);
    }
  };

  const getStatusBadge = () => {
    if (!subscription) return null;
    
    if (subscription.status === 'trial') {
      return (
        <div className={`inline-flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-[#3272F1]/10 border-[#3272F1]/20' : 'bg-blue-50 border-blue-200'} border rounded-full`}>
          <Calendar className="w-4 h-4 text-[#3272F1]" />
          <span className="text-[#3272F1] font-medium">
            {t.trialActive} - {subscription.trial_remaining} {t.daysRemaining}
          </span>
        </div>
      );
    } else if (subscription.status === 'active' || subscription.status === 'active_cancelling') {
      return (
        <div className={`inline-flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-emerald-50 border-emerald-200'} border rounded-full`}>
          <Crown className="w-4 h-4 text-emerald-500" />
          <span className="text-emerald-500 font-medium">{t.subscriptionActive}</span>
        </div>
      );
    } else if (subscription.status === 'cancelled') {
      return (
        <div className={`inline-flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-red-500/10 border-red-500/20' : 'bg-red-50 border-red-200'} border rounded-full`}>
          <XCircle className="w-4 h-4 text-red-500" />
          <span className="text-red-500 font-medium">{t.subscriptionCancelled}</span>
        </div>
      );
    } else if (subscription.status === 'expired' || subscription.status === 'none') {
      return (
        <div className={`inline-flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'} border rounded-full`}>
          <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>{t.expired}</span>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-[#0a0a0f]' : 'bg-gray-50'}`}>
        <Navbar />
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-[#3272F1]" />
        </div>
      </div>
    );
  }

  const isActive = subscription?.active;

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0a0a0f]' : 'bg-gray-50'}`} style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Navbar />
      
      {/* Background effects */}
      {isDark && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#3272F1]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-600/5 rounded-full blur-3xl"></div>
        </div>
      )}
      
      <main className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className={`text-3xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t.title}
          </h1>
          {subscription && (
            <div className="mt-4">
              {getStatusBadge()}
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Pricing Card */}
          {plans.map((plan) => (
            <div 
              key={plan.id} 
              className={`relative ${isDark ? 'bg-gradient-to-br from-gray-900 to-gray-950' : 'bg-white shadow-lg'} rounded-3xl border-2 border-[#3272F1] overflow-hidden`}
            >
              {/* Glow effect */}
              {isDark && <div className="absolute inset-0 bg-[#3272F1]/10 blur-xl"></div>}
              
              {/* Popular badge */}
              <div className="absolute top-6 right-6">
                <div className="flex items-center gap-1 px-3 py-1 bg-[#3272F1] rounded-full text-xs font-medium text-white">
                  <Sparkles className="w-3 h-3" />
                  {t.popular}
                </div>
              </div>
              
              <div className="relative p-8">
                {/* Plan header */}
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-[#3272F1] rounded-xl flex items-center justify-center">
                      <Crown className="w-6 h-6 text-white" />
                    </div>
                    <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{plan.name}</h2>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className={`text-5xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{plan.price}€</span>
                    <span className={isDark ? 'text-gray-500' : 'text-gray-600'}>HT{t.perMonth}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <Zap className="w-4 h-4 text-[#3272F1]" />
                    <span className="text-[#3272F1] font-medium">{t.trialDays}</span>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-3 mb-8">
                  <p className={`font-medium text-xs uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{t.features}</p>
                  <ul className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <div className="w-5 h-5 bg-[#3272F1]/20 rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-[#3272F1]" />
                        </div>
                        <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{translateFeature(feature)}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA */}
                {!isActive ? (
                  <div className="space-y-3">
                    <Button
                      onClick={handleStartTrial}
                      disabled={processing}
                      className="w-full bg-[#3272F1] hover:bg-[#2560d9] gap-2 py-6 text-base"
                      data-testid="start-trial-btn"
                    >
                      {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                      {t.startTrial}
                    </Button>
                    <Button
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={processing}
                      variant="outline"
                      className={`w-full gap-2 py-6 text-base ${isDark ? 'bg-transparent border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                      data-testid="subscribe-btn"
                    >
                      <CreditCard className="w-4 h-4" />
                      {t.subscribe}
                    </Button>
                    
                    {/* Promo Code Section */}
                    <div className={`mt-4 pt-4 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                      <p className={`text-sm mb-3 flex items-center gap-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        <Ticket className="w-4 h-4" />
                        {t.havePromoCode}
                      </p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                          placeholder={t.promoPlaceholder}
                          className={`flex-1 px-4 py-2 rounded-lg border text-sm font-mono tracking-wider ${
                            isDark 
                              ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' 
                              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                          } focus:outline-none focus:ring-2 focus:ring-[#3272F1]/50`}
                          data-testid="promo-code-input"
                        />
                        <Button
                          onClick={handleRedeemPromo}
                          disabled={redeemingPromo || !promoCode.trim()}
                          className="bg-emerald-500 hover:bg-emerald-600 text-white px-4"
                          data-testid="redeem-promo-btn"
                        >
                          {redeemingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className={`${isDark ? 'bg-[#3272F1]/10 border-[#3272F1]/20' : 'bg-blue-50 border-blue-200'} border rounded-xl p-4 text-center`}>
                      <Check className="w-8 h-8 text-[#3272F1] mx-auto mb-2" />
                      <p className="font-medium text-[#3272F1]">{t.alreadySubscribed}</p>
                    </div>
                    <Button
                      onClick={() => setShowCancelModal(true)}
                      variant="outline"
                      className={`w-full gap-2 py-4 ${isDark ? 'bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300' : 'border-red-200 text-red-600 hover:bg-red-50'}`}
                      data-testid="cancel-subscription-btn"
                    >
                      <XCircle className="w-4 h-4" />
                      {t.cancelSubscription}
                    </Button>
                  </div>
                )}

                {/* No commitment notice */}
                <p className={`text-center text-sm mt-4 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                  {t.noCommitment} • {t.cancelAnytime}
                </p>
              </div>
            </div>
          ))}

          {/* Info Card */}
          <div className={`${isDark ? 'bg-gradient-to-br from-gray-900 to-gray-950 border-gray-800' : 'bg-white border-gray-200 shadow-sm'} rounded-2xl border p-8`}>
            <h2 className={`text-xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {language === 'fr' ? 'Comment ça marche ?' : 'How does it work?'}
            </h2>
            
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#3272F1] flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">1</span>
                </div>
                <div>
                  <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {language === 'fr' ? 'Essai gratuit de 30 jours' : '30-day free trial'}
                  </p>
                  <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                    {language === 'fr' 
                      ? 'Testez toutes les fonctionnalités sans engagement'
                      : 'Test all features with no commitment'}
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#3272F1] flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">2</span>
                </div>
                <div>
                  <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {language === 'fr' ? 'Facturation mensuelle' : 'Monthly billing'}
                  </p>
                  <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                    {language === 'fr' 
                      ? '9€ HT/mois après l\'essai gratuit'
                      : '€9 excl. VAT/month after free trial'}
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#3272F1] flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">3</span>
                </div>
                <div>
                  <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {language === 'fr' ? 'Annulez quand vous voulez' : 'Cancel anytime'}
                  </p>
                  <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                    {language === 'fr' 
                      ? 'Sans frais de résiliation'
                      : 'No cancellation fees'}
                  </p>
                </div>
              </div>
            </div>

            {/* Security badge */}
            <div className={`mt-8 pt-6 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
              <div className={`flex items-center gap-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                <Shield className="w-5 h-5" />
                <span className="text-sm">
                  {language === 'fr' 
                    ? 'Paiement sécurisé par Stripe'
                    : 'Secure payment by Stripe'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'} border rounded-2xl p-6 max-w-md w-full shadow-xl`}>
            <div className="text-center mb-6">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${isDark ? 'bg-red-500/10' : 'bg-red-50'}`}>
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t.cancelSubscription}
              </h3>
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                {t.cancelConfirm}
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => setShowCancelModal(false)}
                variant="outline"
                className={`flex-1 ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : ''}`}
                disabled={cancelling}
              >
                {language === 'fr' ? 'Non, garder' : 'No, keep it'}
              </Button>
              <Button
                onClick={handleCancelSubscription}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                disabled={cancelling}
                data-testid="confirm-cancel-btn"
              >
                {cancelling ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  language === 'fr' ? 'Oui, annuler' : 'Yes, cancel'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscription;
